import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { AppStoreModule } from '@/store';
import { TenantUser, TenantDetailsApi, TenantUserInvitation } from '@/common/models';
import IAdminService from '@/services/http/interfaces/IAdminService';
import { UsersHelper } from '@/common/helpers/UsersHelper';
import { UserRoleAssignment } from '@/common/models/UserRoleAssignment';
import { UserDetailsForm } from '@/common/viewModels/userDetails';
import { UserDetail } from '@/common/models/UserDetail';
import { UserDetailsHelper } from '@/components/userDetails/userDetailsHelper';
import UserDetailsRole from '@/common/models/UserDetailsRole';

export interface IUserDetailsState {
  user: TenantUser | null;
  userRoleAssignments: UserRoleAssignment[];
  userDetails: UserDetail;
  filter: string;
  currentPage: number;
  editForm: UserDetailsForm;
  tenantUserEditError: string;
  tenantUserDeleteError: string;
  tenantDetails: TenantDetailsApi;
  tenantUserInvitation: TenantUserInvitation | null;
}

@Module({
  name: 'userDetails',
  namespaced: true,
})
export default class UserDetailsStore extends VuexModule implements IUserDetailsState {

  // State
  user: TenantUser | null = null;
  filter: string = '';
  currentPage = 0;
  userRoleAssignments: UserRoleAssignment[] = [];
  tenantUserInvitation: TenantUserInvitation | null = null;
  userDetails: UserDetail = {} as UserDetail;
  editForm: UserDetailsForm = new UserDetailsForm();
  tenantUserEditError = '';
  tenantUserDeleteError = '';
  tenantUserInviteError = '';
  tenantDetails = {} as TenantDetailsApi;

  // Getters
  get filteredUserDetails(): UserDetail {
    const filterAsLowercase: string = this.filter.toLowerCase();
    if (this.userDetails!.roles!) {
      const filteredRoles: UserDetailsRole[] = this.userDetails!.roles!.filter((t) =>
        (t.scope && t.scope.toLowerCase().includes(filterAsLowercase)) ||
        (t.role && t.role.toLowerCase().includes(filterAsLowercase)));
      const filteredDetails: UserDetail = UserDetailsHelper.createFilteredView(this.tenantDetails!, this.userRoleAssignments!, this.userDetails, filteredRoles);
      return filteredDetails;
    }
    return {} as UserDetail;
  }

  get isEditTenantFormValid(): boolean {
    return this.editForm.state.tenantUserEdit.isValid;
  }

  get selectedUser(): TenantUser {
    return this.user!;
  }

  // Mutations

  @Mutation
  SET_TENANT_USER_ROLE_ASSIGNMENTS(userRoleAssignments: UserRoleAssignment[]) {
    this.userRoleAssignments = userRoleAssignments;
  }

  @Mutation
  SET_TENANT_USER_DETAILS(userDetails: UserDetail) {
    this.userDetails = userDetails;
  }

  @Mutation
  SET_FILTER(filter: string) {
    this.currentPage = 0;
    this.filter = filter;
  }

  @Mutation
  SET_CURRENT_PAGE(currentPage: number) {
    this.currentPage = currentPage;
  }

  @Mutation
  SET_SELECTED_TENANT_USER(user: TenantUser) {
    this.user = user;
  }

  @Mutation
  LOAD_TENANT_USER_FORM(tenantUser: TenantUser) {
    Object.assign(this.editForm.data.tenantUserEdit, {
      name: tenantUser.name,
      email: tenantUser.email,
      role: tenantUser.role,
      firstName: tenantUser.firstName,
      lastName: tenantUser.lastName,
    });
  }

  @Mutation
  async UPDATE_EDIT_TENANT_USER_FORM_STATE() {
    await this.editForm.state.tenantUserEdit.validate(true, this.editForm.data.tenantUserEdit);
  }

  @Mutation
  SET_EDIT_TENANT_USER_FORM_DO_VALIDATION(doValidation: boolean) {
    this.editForm.doValidation = doValidation;
  }

  @Mutation
  SET_EDIT_TENANT_USER_ERROR(errorMessage: string): void {
    this.tenantUserEditError = errorMessage;
  }

  @Mutation
  UPDATE_EDIT_USER_FORM_FIELD_BY_NAME(payload: { fieldName: string, value: any }) {
    (this.editForm.data.tenantUserEdit as any)[payload.fieldName] = payload.value;
  }

  @Mutation
  SET_DELETE_TENANT_USER_ERROR(errorMessage: string): void {
    this.tenantUserDeleteError = errorMessage;
  }

  @Mutation
  SET_TENANT_DETAILS(tenantDetails: TenantDetailsApi) {
    this.tenantDetails = tenantDetails;
  }

  @Mutation
  SET_TENANT_USER_INVITATION(tenantUserInvitation: TenantUserInvitation) {
    this.tenantUserInvitation = tenantUserInvitation;
  }

  @Mutation
  SET_TENANT_USER_INVITE_ERROR(errorMessage: string): void {
    this.tenantUserInviteError = errorMessage;
  }

  // Actions
  @Action
  async getUser(payload: { adminService: IAdminService, tenantId: string, userId: string }) {

    AppStoreModule.SET_ISLOADING(true);

    await payload.adminService.getTenantUser(payload.tenantId, payload.userId)
      .then((user: TenantUser) => {
        this.LOAD_TENANT_USER_FORM(user);
        this.SET_SELECTED_TENANT_USER(user);
      })
      .catch(() => {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to load user');
      })
      .finally(() => {
        AppStoreModule.SET_ISLOADING(false);
      });
  }

  @Action
  setDeleteUser(user: TenantUser): void {
    this.SET_SELECTED_TENANT_USER(user);
  }

  @Action
  async requestTenantUserEdit(payload: { adminService: IAdminService, tenantId: string }): Promise<boolean> {
    let isSuccessful: boolean = false;

    if (!this.editForm.doValidation) {
      this.SET_EDIT_TENANT_USER_FORM_DO_VALIDATION(true);
      await Promise.all([
        this.UPDATE_EDIT_TENANT_USER_FORM_STATE(),
      ]);
    }

    if (this.isEditTenantFormValid) {
      AppStoreModule.SET_ISLOADING(true);

      try {
        const tenantUser: TenantUser = UsersHelper.getUpdateTenantUser(this.user!, this.editForm.data.tenantUserEdit);
        await payload.adminService.updateTenantUser(payload.tenantId, tenantUser);
        isSuccessful = true;
      } catch (error) {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to update tenant user');
      } finally {
        AppStoreModule.SET_ISLOADING(false);
      }
    }
    return isSuccessful;
  }

  @Action
  async requestTenantUserDelete(payload: { adminService: IAdminService, tenantId: string }): Promise<boolean> {
    let isSuccessful: boolean = false;
    AppStoreModule.SET_ISLOADING(true);
    try {
      await payload.adminService.deleteTenantUser(payload.tenantId, this.user!);
      isSuccessful = true;
    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to delete tenant user');
    } finally {
      AppStoreModule.SET_ISLOADING(false);
    }
    return isSuccessful;
  }

  @Action
  async getTenantUserInvitations(payload: { adminService: IAdminService, tenantId: string, userId: string }) {

    AppStoreModule.SET_ISLOADING(true);

    await payload.adminService.getTenantUserInvitations(payload.tenantId, payload.userId)
      .then((invitations: TenantUserInvitation[]) => {
        if (invitations.length > 0) {
          const mostRecentInvitation = UserDetailsHelper.getPendingInvitation(invitations);
          const invitation = UserDetailsHelper.createInvitationView(mostRecentInvitation);
          this.SET_TENANT_USER_INVITATION(invitation);
        } else {
          this.SET_TENANT_USER_INVITATION(new TenantUserInvitation());
        }
      })
      .catch(() => {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to load user invitations');
      })
      .finally(() => {
        AppStoreModule.SET_ISLOADING(false);
      });
  }

  @Action
  async sendInvitation(payload: { adminService: IAdminService, tenantId: string }): Promise<boolean> {
    let isSuccessful: boolean = false;
    try {
      const currentUser: TenantUser = UserDetailsHelper.createTenantUserModal(this.userDetails!);
      currentUser!.isResendProcess = true;
      await payload.adminService.inviteTenantUser(payload.tenantId, currentUser!);
      isSuccessful = true;
    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to invite user');
    } finally {
      AppStoreModule.SET_ISLOADING(false);
    }
    return isSuccessful;
  }

  @Action
  async editUserModalFormInputChange(payload: { fieldName: string, value: any }) {
    this.UPDATE_EDIT_USER_FORM_FIELD_BY_NAME(payload);
    await this.UPDATE_EDIT_TENANT_USER_FORM_STATE();
  }

  @Action
  updateEditTenantUserError(errorMessage: string): void {
    this.SET_EDIT_TENANT_USER_ERROR(errorMessage);
  }

  @Action
  updateDeleteTenantUserError(errorMessage: string): void {
    this.SET_DELETE_TENANT_USER_ERROR(errorMessage);
  }

  @Action
  updateInviteTenantUserError(errorMessage: string): void {
    this.SET_TENANT_USER_INVITE_ERROR(errorMessage);
  }

  @Action({ rawError: true })
  async getUserDetails(payload: { adminService: IAdminService, tenantId: string, userId: string }) {
    AppStoreModule.SET_ISLOADING(true);
    await Promise.all([
      await this.getUser({ adminService: payload.adminService, tenantId: payload.tenantId, userId: payload.userId }),
      await this.getUserRoleAssignments({ adminService: payload.adminService, tenantId: payload.tenantId, userId: payload.userId }),
    ]).then(() => {
      const fetchedUserDetails: UserDetail = new UserDetail(this.user!, this.userRoleAssignments!);
      const userDetails: UserDetail = UserDetailsHelper.createUserDetailsView(this.tenantDetails!, fetchedUserDetails, this.userRoleAssignments!);
      this.SET_TENANT_USER_DETAILS(userDetails);
      this.SET_TENANT_DETAILS(this.context.rootState.tenantDetails);
    }).catch(() => {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to load UserDetails');
    }).finally(() => {
      AppStoreModule.SET_ISLOADING(false);
    });
  }

  @Action
  async getUserRoleAssignments(payload: { adminService: IAdminService, tenantId: string, userId: string }) {
    await payload.adminService.getUserRoleAssignments(payload.tenantId, payload.userId)
      .then((userRoleAssignments: UserRoleAssignment[]) => {
        this.SET_TENANT_USER_ROLE_ASSIGNMENTS(userRoleAssignments);
      })
      .catch(() => {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to load user role assignments');
      });
  }

  @Action
  setFilter(filter: string) {
    this.SET_FILTER(filter);
  }

  @Action
  setCurrentPage(currentPage: number) {
    this.SET_CURRENT_PAGE(currentPage);
  }
}
